import { useState, useRef } from 'react';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import SandhillsReCAPTCHA from '@Components/Shared/SandhillsReCAPTCHA';
import { gtmPushEvent, sandhillsRequest, focusErroredField } from '@Components/Utilities/SandhillsUtility';
import TextField from '@Components/Shared/Controls/TextField';
import SimpleHomeBreadcrumb from '@Components/Shared/SimpleHomeBreadcrumb';
import TranslationText from '@Components/Utilities/TranslationText';
import SelectField from '@Components/Shared/Controls/SelectField';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import { SandhillsSlickSlider } from '@Components/Shared/SandhillsSlickSlider';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';

const MachineryAdvertisingView = (props) => {

    const recaptchaRef = useRef(null);
    const [captchaOpen, setCaptchaOpen] = useState(false);
    const windowSize = useWindowSize();
    const [success, setSuccess] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [hideForm, setHideForm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [formData, setFormData] = useState({
        FirstName: props.FirstName || '',
        LastName: props.LastName || '',
        Email: props.Email || '',
        Phone: props.Phone || '',
        Company: props.Company || '',
        City: props.City || '',
        State: props.State || '',
        Country: '',
        PostalCode: props.PostalCode || '',
        TotalUsed: '',
        TotalNew: '',
        HowCanWeHelpYouItems: props.HowCanWeHelpYouItems || [],
        ModelType: props.ModelType
    });
    const inputReferences = useRef({});
    const routes = useRecoilValue(atoms.routes);
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const tld = currentSite.TopLevelDomain;

    var contactPhone = "";
    var formattedPhone = "";
    var alternateFormattedPhone = "";

    if (tld == ".com") {
        contactPhone = "18002474898";
        formattedPhone = "800-247-4898";
        alternateFormattedPhone = "(800) 247-4898";
    } else if (tld == ".ie" || tld == ".co.uk" || tld == ".pl" || tld == ".li" || tld == ".ch") {
        contactPhone = "4401618718760";
        formattedPhone = "+44 (0) 161 871 8760";
        alternateFormattedPhone = "+44 (0) 161 871 8760";
    } else if (tld == ".com.au") {
        contactPhone = "610730560831";
        formattedPhone = "+61 (0)7 3056 0831";
        alternateFormattedPhone = "+61 (0)7 3056 0831";
    } else if (tld == ".fr" || tld == ".be") {
        contactPhone = "330322902200";
        formattedPhone = "+33 (0) 3 22 90 22 00";
        alternateFormattedPhone = "+33 (0) 3 22 90 22 00";
    } else if (tld == ".nl") {
        contactPhone = "318000232782";
        formattedPhone = "+31 800 0232782";
        alternateFormattedPhone = "+31 800 0232782";
    } else if (tld == ".es") {
        contactPhone = "34900838707";
        formattedPhone = "+34 900 83 87 07";
        alternateFormattedPhone = "+34 900 83 87 07";
    } else if (tld == ".at" || tld == ".de") {
        contactPhone = "4904030187517";
        formattedPhone = "+49 (0)40 3018 7517";
        alternateFormattedPhone = "+49 (0)40 3018 7517";
    } else if (tld == ".it") {
        contactPhone = "390289352800";
        formattedPhone = "+39 02 8935 2800";
        alternateFormattedPhone = "+39 02 8935 2800";
    }


    const submitForm = (e) => {
        e.preventDefault();
        let errors = run(formData, [
            ruleRunner('Email', 'Email', ValidationRules.requiredOr('Phone', 'Phone')),
            ruleRunner('Phone', 'Phone', ValidationRules.requiredOr('Email', 'Email')),
        ]);

        if (formData?.HowCanWeHelpYouItems?.filter(f => f.Selected)?.length <= 0) {
            errors['CheckboxError'] = 'Please select at least one checkbox option';
        }

        setFormData({
            ...formData,
            TotalUsed: formData.TotalUsed ? formData.TotalUsed : 0,
            TotalNew: formData.TotalNew ? formData.TotalNew : 0,
        });

        setValidationErrors(errors);
        focusErroredField(errors, inputReferences);
        setSubmitted(false);

        if (Object.keys(errors).length <= 0) {
            setSuccess(false);
            if (props.Captcha && recaptchaRef.current) {
                setCaptchaOpen(true);
                recaptchaRef.current.execute();
            } else {
                onSubmit();
            }
        }
    };

    const onSubmit = (token) => {
        setIsLoading(true);
        sandhillsRequest({
            url: routes.SubmitAdvertising,
            method: 'post',
            data: {
                ...formData,
                Captcha: {
                    Response: token
                },
                webAgreementTypeIDs: props.WebAgreementTypeIDs
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            setIsLoading(false);
            if (response.Success) {
                gtmPushEvent('advertising-form');
            }

            setFormData({
                FirstName: response.FirstName || '',
                LastName: response.LastName || '',
                Email: response.Email || '',
                Phone: response.Phone || '',
                Company: response.Company || '',
                City: response.City || '',
                State: response.State || '',
                Country: '',
                PostalCode: response.PostalCode || '',
                TotalUsed: response.Success ? '' : response.TotalUsed.toString(),
                TotalNew: response.Success ? '' : response.TotalNew.toString(),
                HowCanWeHelpYouItems: props.HowCanWeHelpYouItems || [],
                ModelType: response.ModelType
            });

            setSuccess(response.Success);
            setSubmitted(true);
            setValidationErrors(response.ValidationErrors || {});
            focusErroredField(response.ValidationErrors, inputReferences);

            if (response.UseLeadsUXUpdate && response.Success) {
                setHideForm(true);
                setTimeout(() => {
                    setHideForm(false);
                    setSuccess(false);
                    setSubmitted(false);
                }, 3000);
            }

            if (response.Captcha && recaptchaRef.current) {
                setCaptchaOpen(false);
                recaptchaRef.current.reset();
            }
        });
    };

    const onTextChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
    };

    const onCheckboxChange = (e) => {
        let value = e.target.value;
        let checked = e.target.checked;
        setFormData(previousState => {
            return {
                ...previousState,
                HowCanWeHelpYouItems: [...previousState.HowCanWeHelpYouItems].map(item => {
                    if (item.Value == value) {
                        return { ...item, Selected: checked };
                    } else {
                        return { ...item };
                    }
                })
            };
        });
    };

    const mtAdvertising = {
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5000,
        responsive: null,
        infinite: true
    };

    return (
        <div className='advertising'>
            <div className='info-content-container'>
                <div className='info-content-header'>
                    <SimpleHomeBreadcrumb homeRoute={props.BreadCrumbHomeRoute} displayText={<TranslationText tKey='sandhills.tradepubwebsite.mvc.advertising' />} />
                </div>
                <div className='advertising-container'>
                    <h1 className='pagename border-bottom'>{translator.translate('sandhills.tradesites.mvc.advertisingwithsite', [currentSite.ProperDisplayDomainName])}</h1>
                    <div className='description-container border-bottom'>

                        <div className='advertising-description'>
                            <div className='bottom-padding'>Buyers looking for new and used construction equipment for sale and dealers who want to reach prospective customers worldwide all turn to the industry’s prime marketplace for buying and selling construction equipment: Machinery Trader.</div>
                            <div className='bottom-padding'>MachineryTrader.com is continually updated with new listings for excavators, wheel loaders, dozers, forklifts, skid steers, telehandlers, loader backhoes, lifts, motor graders, off-highway trucks, aggregate equipment, attachments, and much more. Parts and attachments are also sold through MachineryTrader.com.</div>
                            <div className='bottom-padding'>The weekly <i>Machinery Trader</i> magazine complements MachineryTrader.com, reaching construction equipment buyers directly in their places of business.  <i>Machinery Trader</i> is printed in multiple regional editions, enabling sellers to reach specific markets.</div>
                            <div className='bottom-padding'>To learn more about selling your construction equipment with Machinery Trader, call <a href={"tel:+" + contactPhone}>{formattedPhone}</a> or contact us online using the form below.</div>
                        </div>
                        {props.AdvertisingImageUrl &&
                            <div className='advertising-image'>
                                <img src={props.AdvertisingImageUrl} alt='Advertising' />
                            </div>}
                    </div>
                    {(props.SlideShowCards && props.SlideShowCards.length > 0) &&
                        <div className='slideshow'>
                            <ul className='cycle-slideshow'>
                            <SandhillsSlickSlider settings={mtAdvertising} {...props}>
                                    {props.SlideShowCards.map((card, index) =>
                                        <li key={index}>
                                            <div className="Container">
                                                <div className="slide-text">
                                                    <h2>{card.ImageTitle}</h2>
                                                    <p>{card.ImageParagraph}</p>
                                                </div>
                                                <div className="slide-image">
                                                    <img src={card.ImageUrl} alt={card.AltText} />
                                                </div>
                                            </div>
                                        </li>)}
                                </SandhillsSlickSlider>
                            </ul>
                        </div>}
                </div>
            </div>
            <div className='advertising-form'>
                <div className='info-content-container cf'>
                    <h2>{translator.translate('sandhills.tradesites.mvc.interestedinadvertising', [currentSite.ProperDisplayDomainName.replace(currentSite.TopLevelDomain, '')])}</h2>
                    <h3 className='bottom-padding' dangerouslySetInnerHTML={{ __html: translator.translate('sandhills.tradesites.mvc.pleasecallorfillouttheform', [contactPhone, alternateFormattedPhone]) }}></h3>
                    {props.UseLeadsUXUpdate && success &&
                        <div className='success' style={{ textAlign: 'center' }}>Successfully sent inquiry!</div>}
                    <form id='feedbackForm' className={(hideForm ? 'temp-gone' : '')} onSubmit={submitForm}>
                        <ValidationErrors errors={validationErrors} />
                        <div className='column7 left'>
                            <div className='column6 left'>
                                <TextField
                                    id='FirstName'
                                    name='FirstName'
                                    value={formData.FirstName}
                                    placeholder='First Name'
                                    type='text'
                                    label='First Name'
                                    class='modal-form-el'
                                    validationErrors={validationErrors.FirstName}
                                    ref={(element) => inputReferences.current[`FirstName`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <TextField
                                    id='LastName'
                                    name='LastName'
                                    value={formData.LastName}
                                    placeholder='Last Name'
                                    type='text'
                                    label='Last Name'
                                    class='modal-form-el'
                                    validationErrors={validationErrors.LastName}
                                    ref={(element) => inputReferences.current[`LastName`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <TextField
                                    id='Email'
                                    name='Email'
                                    value={formData.Email}
                                    placeholder='Email'
                                    type='text'
                                    label='Email'
                                    class='modal-form-el'
                                    required={true}
                                    validationErrors={validationErrors.Email}
                                    ref={(element) => inputReferences.current[`Email`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <TextField
                                    id='Phone'
                                    name='Phone'
                                    value={formData.Phone}
                                    placeholder='(000) 000-0000'
                                    type='text'
                                    label='Phone'
                                    class='modal-form-el'
                                    required={true}
                                    validationErrors={validationErrors.Phone}
                                    ref={(element) => inputReferences.current[`Phone`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <TextField
                                    id='Company'
                                    name='Company'
                                    value={formData.Company}
                                    placeholder='Company'
                                    type='text'
                                    label='Company'
                                    class='modal-form-el'
                                    validationErrors={validationErrors.Company}
                                    ref={(element) => inputReferences.current[`Company`] = element}
                                    onFieldChanged={onTextChange}
                                />
                            </div>
                            <div className='column6 left'>
                                <TextField
                                    id='City'
                                    name='City'
                                    value={formData.City}
                                    placeholder='City'
                                    type='text'
                                    label='City'
                                    class='modal-form-el'
                                    validationErrors={validationErrors.City}
                                    ref={(element) => inputReferences.current[`City`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                {props.GetStateDropDown && props.GetStateDropDown.length > 0 &&
                                    <>
                                        <SelectField
                                            id='State'
                                            name='State'
                                            value={formData.State}
                                            options={props.GetStateDropDown}
                                            label='State'
                                            class='modal-form-el'
                                            selectClass='select-dropdown'
                                            validationErrors={validationErrors.State}
                                            ref={(element) => inputReferences.current[`State`] = element}
                                            onFieldChanged={onTextChange}
                                        />
                                    </>}
                                <TextField
                                    id='Country'
                                    name='Country'
                                    value={formData.Country}
                                    placeholder='Country'
                                    type='text'
                                    label={<TranslationText tKey='sandhills.country' />}
                                    class='modal-form-el hidden'
                                    validationErrors={validationErrors.Country}
                                    ref={(element) => inputReferences.current[`Country`] = element}
                                    onFieldChanged={onTextChange}
                                    htmlAutoComplete='chrome-off'
                                />
                                <TextField
                                    id='PostalCode'
                                    name='PostalCode'
                                    value={formData.PostalCode}
                                    placeholder='Zip'
                                    type='text'
                                    label='Zip'
                                    class='modal-form-el'
                                    validationErrors={validationErrors.PostalCode}
                                    ref={(element) => inputReferences.current[`PostalCode`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <TextField
                                    id='TotalUsed'
                                    name='TotalUsed'
                                    value={formData.TotalUsed}
                                    placeholder='Total Used Construction Equipment'
                                    type='number'
                                    label='Total Used Construction Equipment'
                                    class='modal-form-el'
                                    inputClass='number-input'
                                    validationErrors={validationErrors.TotalUsed}
                                    ref={(element) => inputReferences.current[`TotalUsed`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <TextField
                                    id='TotalNew'
                                    name='TotalNew'
                                    value={formData.TotalNew}
                                    placeholder='Total New Construction Equipment'
                                    type='number'
                                    label='Total New Construction Equipment'
                                    class='modal-form-el'
                                    inputClass='number-input'
                                    validationErrors={validationErrors.TotalNew}
                                    ref={(element) => inputReferences.current[`TotalNew`] = element}
                                    onFieldChanged={onTextChange}
                                />
                            </div>
                        </div>
                        <div className='column4 left checkbox-wrapper'>
                            <fieldset>
                                <legend className='ts__legend'><strong>{translator.translate('sandhills.tradesites.mvc.howcanwehelpyou')}</strong></legend>
                                {formData.HowCanWeHelpYouItems.map(item =>
                                    <div className='checkbox-container'>
                                        <input id={'HowCanWeHelpYouItems-' + item.Text} name={'HowCanWeHelpYouItems-' + item.Text} className='checkbox' value={item.Value} type='checkbox' checked={item.Selected} onChange={onCheckboxChange} />
                                        <label htmlFor={'HowCanWeHelpYouItems-' + item.Text}>{item.Text}</label>
                                    </div>
                                )}
                                <div className='error'>{validationErrors.CheckboxError}</div>
                            </fieldset>
                        </div>
                        <div className='clear' />
                        <div className='column12 left'>
                            {submitted &&
                                (success ?
                                    <div className='success'>Successfully sent inquiry!</div> :
                                    <div className='error'>An error occured, please try again.</div>)}
                        </div>
                        <div className='clear' />
                        <div className='column12 left'>
                            <div className='recaptcha-submit-container'>
                                <div className='captcha-placeholder'>
                                    {props.Captcha &&
                                        <SandhillsReCAPTCHA
                                            ref={recaptchaRef}
                                            sitekey={props.Captcha.Key}
                                            onChange={onSubmit}
                                            captchaOpen={captchaOpen}
                                        />}
                                </div>
                                <div dangerouslySetInnerHTML={{ __html: props.FeedbackPolicyNotice }} style={{ 'font-size': '.75em', 'margin-top': '10px' }} />
                                <input className='recaptcha-submit-button button ts-button' type='submit' id='advertising-submit-button' name='advertising-submit-button' value='Submit' />
                            </div>
                        </div>
                    </form>
                </div>
            </div>
            <Backdrop
                open={isLoading}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress />
            </Backdrop>
        </div>
    );
};

export default MachineryAdvertisingView;
global.MachineryAdvertisingView = MachineryAdvertisingView;